// src/features/places/marketInventory.js
//
// PLACES-SLICE-2: canonical market <-> office membership and geography.
//
// Membership is read from the FULL office inventory (GET /api/locations via
// getLocations), never inferred from the plan payload, which can omit
// offices. An office belongs to a market when location.marketId === market.id
// and its stage is not closed (vacated and in-build offices remain
// members). Draft offices are browsable but not counted (see selectors).
//
// Geography fallback (deterministic, no invented pins):
//   1. A market pin is the arithmetic mean of its member offices' effective
//      coordinates (geoLat/geoLng, else legacy lat/lng), using only valid
//      values (|lat|<=90, |lng|<=180, not 0,0). Rounded to 5 decimals.
//   2. If no member has valid coordinates the market is reported as
//      unmapped and listed beside the map; no coordinate is guessed from
//      the city name or region.

import { useCallback, useEffect, useState } from "react";
import { getLocations } from "../../data/locationsClient.js";
import { effectiveLat, effectiveLng } from "../locations/OfficeMapFoundation.jsx";
import { log } from "../../utils/logger.js";
import { isMarketMember, isCountedMarketOffice } from "../plan/marketsService.js";

// Two selectors, never one: browse membership (isMarketMember — only closed
// excluded, so Draft scaffolding is browsable with its honest stage) and
// effective office counts (isCountedMarketOffice — Draft excluded too).
export function isCanonicalMember(location, marketId) {
  return Boolean(location && marketId && location.marketId === marketId && isMarketMember(location));
}

const STAGE_RANK = { operational: 0, dormant: 1, build: 2, vacated: 3, draft: 4 };

function sortOffices(list) {
  return list.sort((a, b) =>
    (STAGE_RANK[a.stage] ?? 9) - (STAGE_RANK[b.stage] ?? 9)
    || Number(Boolean(b.isHub)) - Number(Boolean(a.isHub))
    || String(a.locationAlias ?? a.id).localeCompare(String(b.locationAlias ?? b.id)));
}

// Browse list for the market record (includes Draft).
export function canonicalMarketOffices(locations, marketId) {
  return sortOffices((locations ?? []).filter((l) => isCanonicalMember(l, marketId)));
}

// Effective office count population (tiles, map, chrome, summary).
export function countedMarketOffices(locations, marketId) {
  return sortOffices((locations ?? []).filter((l) =>
    l && marketId && l.marketId === marketId && isCountedMarketOffice(l)));
}

export function validCoordinate(location) {
  const lat = effectiveLat(location);
  const lng = effectiveLng(location);
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
}

export function marketGeography(members) {
  const coords = (members ?? []).map(validCoordinate).filter(Boolean);
  if (coords.length === 0) return null;
  const round = (n) => Math.round(n * 1e5) / 1e5;
  const lat = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lng = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return { coordinates: [round(lat), round(lng)], basis: coords.length };
}

export function useOfficeInventory(enabled = true) {
  const [state, setState] = useState({ locations: [], loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    getLocations()
      .then((list) => {
        if (cancelled) return;
        // No silent fallback: a malformed payload is an error, not "0 offices".
        if (!Array.isArray(list)) {
          log.error("useOfficeInventory: malformed getLocations payload", { type: typeof list });
          setState({ locations: [], loading: false, error: "Office inventory response was malformed" });
          return;
        }
        setState({ locations: list, loading: false, error: null });
      })
      .catch((err) => {
        log.error("useOfficeInventory: getLocations failed", { err: String(err) });
        if (!cancelled) setState({ locations: [], loading: false, error: err?.message ?? "Failed to load offices" });
      });
    return () => { cancelled = true; };
  }, [nonce, enabled]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
